import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import * as React from "react";

import {
  addExpiryContext,
  dismissManagerCard,
  fetchDealDashboard,
  listManagerDeals,
  snoozeExpiryNotice,
  type CloseReasonCategory,
  type DealDashboardMetrics,
  type DealStatus,
  type ManagerDealListItem,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { ManagerDealReviewDrawer } from "@/components/deals/ManagerDealReviewDrawer";
import { ManagerDealsLedger } from "@/components/deals/manager/ManagerDealsLedger";
import { ManagerFeedSidebar } from "@/components/deals/manager/ManagerFeedSidebar";
import { ManagerPerformancePanel } from "@/components/deals/manager/ManagerPerformancePanel";
import { buildManagerFeed } from "@/components/deals/manager/sla";
import { Button } from "@/components/ui/button";
import { dealToasts, useToast } from "@/components/ui/toast";
import type { ManagerDealTab } from "@/components/deals/manager/managerDealTabs";

// CHP-804: the known DealStatus set, used to validate the ?status deep-link
// before it's applied as a filter (an unknown value silently empties the tab).
const DEAL_STATUSES: ReadonlySet<DealStatus> = new Set<DealStatus>([
  "draft",
  "pending_review",
  "live",
  "denied",
  "closed_lost",
  "extension_pending",
  "win_pending_review",
  "closed_won",
]);

function isDealStatus(value: string | undefined): value is DealStatus {
  return value !== undefined && DEAL_STATUSES.has(value as DealStatus);
}

export function ManagerDealsQueueRoute() {
  const { accessToken } = useCurrentUser();
  const { toast } = useToast();

  // Deep-link entry (deals-gap wave 2): a ?review=<dealId> param opens the
  // review drawer on mount (drawer-first pattern). A ?status=<deal_status> param
  // pre-applies a precise status filter on the ledger ("Smart Navigator" deep
  // link). Both still apply from a bookmark / notification even though the
  // manager Overview that originally drove them was removed (review-r2). No
  // validateSearch on this route — read loosely like deals-new.tsx does for
  // ?draftId.
  const search = useSearch({ strict: false }) as {
    review?: string;
    status?: string;
  };
  // CHP-804: validate ?status against the known DealStatus set. A malformed /
  // unknown value previously set the filter to a status no row carries,
  // silently rendering an empty "Pending Review" tab; ignore unknowns and fall
  // back to the default (no narrowing).
  const deepLinkStatus = isDealStatus(search.status) ? search.status : null;
  const [activeTab, setActiveTab] = React.useState<ManagerDealTab>(
    deepLinkStatus ? "to_be_reviewed" : "all",
  );
  const [searchValue, setSearchValue] = React.useState("");
  // A precise status narrowing layered on top of the tab/search filter, set
  // only by a pulse-card deep-link. Cleared the moment the user changes tab or
  // searches (handled in the ledger), so it never sticks past the first view.
  const [statusFilter, setStatusFilter] = React.useState<ManagerDealListItem["deal_status"] | null>(
    deepLinkStatus,
  );
  // Row-click + feed buttons open the review in a right-hand drawer (CHP-223);
  // the standalone /dashboard/manager/deals/$dealId route stays for direct links.
  const [reviewDealId, setReviewDealId] = React.useState<string | null>(search.review ?? null);
  // "Search deals" scrolls the ledger's search input into view and focuses it.
  const searchRef = React.useRef<HTMLInputElement | null>(null);

  function focusSearch() {
    const input = searchRef.current;
    if (!input) return;
    input.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => input.focus(), 150);
  }

  const deals = useQuery<ManagerDealListItem[]>({
    queryKey: ["deals", "manager-list", accessToken],
    queryFn: () => listManagerDeals(accessToken!, "all"),
    enabled: !!accessToken,
    // CHP-310: passive-watcher backstop — a manager watching the pipeline sees
    // partner-side registrations/wins/closes within 30s without a refocus.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const queryClient = useQueryClient();
  // CHP-369/371/375: dismiss a manager-feed card (✕ / view of a closed-lost).
  const dismissCard = useMutation({
    mutationFn: (dealId: string) => dismissManagerCard(accessToken!, dealId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["deals", "manager-list", accessToken] }),
  });
  // CHP-394: "Remind me in 7 days" snooze on the expiry-notice card.
  const snoozeCard = useMutation({
    mutationFn: (dealId: string) => snoozeExpiryNotice(accessToken!, dealId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["deals", "manager-list", accessToken] }),
  });
  // CHP-380: record a close reason on an auto-expired closed-lost deal.
  const addContext = useMutation({
    mutationFn: (vars: {
      dealId: string;
      payload: { close_reason_category: CloseReasonCategory; close_notes: string | null };
    }) => addExpiryContext(accessToken!, vars.dealId, vars.payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["deals", "manager-list", accessToken] }),
    // CHP-380: surface failures (e.g. a 409 on a non-auto-expired deal) instead
    // of failing silently — the affordance is now gated on auto_expired, but a
    // stale card / race can still 409, so the manager must see it.
    onError: () => toast(dealToasts.failed("add context")),
  });

  const dashboard = useQuery<DealDashboardMetrics>({
    queryKey: ["deals", "dashboard", accessToken],
    queryFn: () => fetchDealDashboard(accessToken!),
    enabled: !!accessToken,
    refetchInterval: 30_000, // CHP-310: keep scorecards in step with the ledger
    refetchIntervalInBackground: false,
  });

  const allDeals = deals.data ?? [];
  const now = new Date();
  const feedItems = buildManagerFeed(allDeals, now);

  function openDeal(dealId: string) {
    setReviewDealId(dealId);
  }

  return (
    <div className="container py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          Manage channel partner deals, end to end.
        </h1>
        <p className="text-sm text-muted-foreground">
          Register deals, process approvals, and act on what&apos;s high-priority to keep momentum
          going.
        </p>
      </header>

      {/* Primary-task buttons (CHP-222), mirroring the BDM/PO dashboard.
          "Register new deal" opens the on-behalf registration wizard (CHP-202);
          "Search deals" focuses the ledger search. */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Button asChild>
          <Link to="/dashboard/manager/deals/new" data-testid="manager-register-deal">
            <Plus className="h-4 w-4" aria-hidden /> Register new deal
          </Link>
        </Button>
        <Button variant="outline" onClick={focusSearch} data-testid="manager-search-deals">
          <Search className="h-4 w-4" aria-hidden /> Search deals
        </Button>
      </div>

      <ManagerPerformancePanel metrics={dashboard.data} isError={dashboard.isError} />

      {/* FEED — SLA-warning to-do cards, full-width above the ledger. Renders a
          zero-state message when empty (conventions sweep review-r2). */}
      <div className="mb-6">
        <ManagerFeedSidebar
          items={feedItems}
          onReviewDeal={openDeal}
          onDismiss={(dealId) => dismissCard.mutate(dealId)}
          onSnooze={(dealId) => snoozeCard.mutate(dealId)}
          onAddExpiryContext={(dealId, payload) => addContext.mutate({ dealId, payload })}
          addingContextDealId={addContext.isPending ? addContext.variables?.dealId : null}
          now={now}
        />
      </div>

      <div>
        {deals.isLoading ? (
          <p role="status">Loading pipeline...</p>
        ) : deals.error ? (
          <p role="alert">Couldn't load the pipeline.</p>
        ) : (
          <ManagerDealsLedger
            deals={allDeals}
            metrics={dashboard.data}
            activeTab={activeTab}
            onTabChange={(tab) => {
              // A pulse-card status narrowing is a one-shot deep-link: any tab
              // change clears it so the chosen tab shows its full contents.
              setStatusFilter(null);
              setActiveTab(tab);
            }}
            statusFilter={statusFilter}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onDealClick={openDeal}
            searchRef={searchRef}
            emptyAction={
              <Button asChild size="sm">
                <Link to="/dashboard/manager/deals/new">
                  <Plus className="h-4 w-4" aria-hidden /> Register a deal
                </Link>
              </Button>
            }
          />
        )}
      </div>

      <ManagerDealReviewDrawer dealId={reviewDealId} onClose={() => setReviewDealId(null)} />
    </div>
  );
}
